import { Box } from "@mui/material";
import { SearchPattern } from "@novorender/data-js-api";
import type { Webviewer } from "lib";
import { ReactNode, useEffect, useRef } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { OfflineConnectionBar, useOfflineBarVariant } from "components/offlineIndicator";
import { DockDragProvider, DockLayoutProvider, DockManager, WindowManager } from "components/window";
import { CameraStateProvider } from "contexts/cameraState";
import { CoachmarksProvider } from "contexts/coachmarks";
import { DrawersProvider } from "contexts/drawers";
import { ExplorerGlobalsProvider, useExplorerGlobals } from "contexts/explorerGlobals";
import { HiddenProvider } from "contexts/hidden";
import { HighlightCollectionsProvider } from "contexts/highlightCollections";
import { HighlightedProvider } from "contexts/highlighted";
import { LastPickSampleProvider } from "contexts/lastPickSample";
import { MarkerRendererContext } from "contexts/markerSvgRenderer/hooks";
import { ObjectGroupsProvider } from "contexts/objectGroups";
import { ObjectTreeProvider } from "contexts/objectTree";
import { isMobileUxFlavor } from "contexts/panels";
import { PickingSessionProvider } from "contexts/pickingSession";
import { ProcoreStampProvider } from "contexts/procoreStamp";
import { PropertiesTabsProvider } from "contexts/propertiesTabs";
import { SelectionBasketProvider } from "contexts/selectionBasket";
import { SidePanelsProvider } from "contexts/sidePanels";
import { SvgRendererContext } from "contexts/svgRenderer";
import { ToolbarProvider } from "contexts/toolbars";
import { WebviewerBridge } from "contexts/webviewer/bridge";
import { WindowsProvider } from "contexts/windows";
import { Consent } from "features/consent";
import { LibSvgRenderer } from "features/engine2D/svgRenderer/LibSvgRenderer";
import { FormsGlobalsProvider } from "features/forms/formsGlobals";
import { Hud } from "features/hud";
import { MarkerCreationMode } from "features/markers/MarkerCreationMode";
import { MarkerRendererManager } from "features/markers/MarkerRendererManager";
import { selectSceneStatus } from "features/render";
import { Render3D } from "features/render/render";
import { VersionAlert } from "features/versionAlert";
import { useEagerDescendantsPreload } from "hooks/useEagerDescendantsPreload";
import { useTopLevelSelectionsSync } from "hooks/useTopLevelSelectionsSync";
import { explorerActions, selectIsProcoreMobileDesign, selectUxFlavor } from "slices/explorer";
import { UrlSearchQuery, UxFlavor } from "slices/explorer/types";
import type { EventSubscriptionOptions, WebviewerEventCallback, WebviewerEventName } from "types/events";
import { AsyncStatus } from "types/misc";
import { getOAuthState } from "utils/auth";

export function Explorer({
    webviewer,
    resolve,
    reject,
}: Readonly<{ webviewer?: Webviewer; resolve?: () => void; reject?: () => void }>) {
    const storeUxFlavor = useAppSelector(selectUxFlavor);
    const uxFlavor = webviewer ? storeUxFlavor : undefined;

    return (
        <ContextProviders uxFlavor={uxFlavor}>
            <ExplorerBase uxFlavor={uxFlavor} />
            {webviewer && resolve && reject && (
                <WebviewerBridge webviewer={webviewer} resolve={resolve} reject={reject} />
            )}
            {!webviewer && <InternalEventEmitterBridge />}
        </ContextProviders>
    );
}

// Bridge component to expose internal EventEmitter in app mode
function InternalEventEmitterBridge() {
    const {
        state: { internalEventEmitter },
    } = useExplorerGlobals();

    useEffect(() => {
        if (!internalEventEmitter) {
            return;
        }

        // Expose internal EventEmitter to globalThis for console access in app mode
        if (!(globalThis as typeof globalThis & { webviewer?: Webviewer }).webviewer) {
            // Create a minimal API surface similar to webviewer.events
            (
                globalThis as typeof globalThis & {
                    webviewerEvents?: {
                        addEventListener: typeof internalEventEmitter.on;
                        removeEventListener: typeof internalEventEmitter.off;
                    };
                }
            ).webviewerEvents = {
                addEventListener: <E extends WebviewerEventName>(
                    eventName: E,
                    callback: WebviewerEventCallback<E>,
                    options?: EventSubscriptionOptions,
                ) => {
                    return internalEventEmitter.on(eventName, callback, options);
                },
                removeEventListener: <E extends WebviewerEventName>(
                    eventName: E,
                    callback: WebviewerEventCallback<E>,
                ) => {
                    internalEventEmitter.off(eventName, callback);
                },
            };
        }

        return () => {
            const global = globalThis as typeof globalThis & { webviewerEvents?: unknown };
            if (global.webviewerEvents) {
                delete global.webviewerEvents;
            }
        };
    }, [internalEventEmitter]);

    return null;
}

const disableHud = new URLSearchParams(window.location.search).get("disableHud") === "true";

function ExplorerBase({ uxFlavor }: { uxFlavor?: UxFlavor }) {
    const dispatch = useAppDispatch();
    const {
        state: { view, scene, internalEventEmitter },
    } = useExplorerGlobals();
    const sceneStatus = useAppSelector(selectSceneStatus);

    // Eagerly preload descendant IDs for top-level nodes so that "Select All"
    // is fast on first click, even before the Object Tree has been opened.
    useEagerDescendantsPreload();
    useTopLevelSelectionsSync();

    const offlineBarVariant = useOfflineBarVariant();
    const isProcoreMobile = useAppSelector(selectIsProcoreMobileDesign);
    // The full-width "intermittent" bar shifts the viewer content down. On mobile
    // the bar is rendered below the in-app header by the mobile HUD instead.
    const showShiftingOfflineBar = offlineBarVariant === "intermittent" && !isProcoreMobile;

    // Emit sceneStatus changes to internalEventEmitter
    useEffect(() => {
        internalEventEmitter.emit("sceneStatus", sceneStatus);
    }, [sceneStatus, internalEventEmitter]);

    useEffect(() => {
        const oAuthState = getOAuthState();

        if (oAuthState) {
            if (oAuthState.service && oAuthState.service !== "self") {
                dispatch(explorerActions.setWidgets([oAuthState.service]));
            }

            if (oAuthState.localBookmarkId) {
                dispatch(explorerActions.setLocalBookmarkId(oAuthState.localBookmarkId));
            }
        } else {
            const searchParams = new URLSearchParams(window.location.search);

            const bookmarkId = searchParams.get("bookmarkId");
            if (bookmarkId) {
                dispatch(explorerActions.setUrlBookmarkId(bookmarkId));
                return;
            }

            dispatch(explorerActions.setUrlSearchQuery(getUrlSearchQuery()));
        }
    }, [dispatch]);

    return (
        <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column" }}>
            {showShiftingOfflineBar && <OfflineConnectionBar />}
            <Box sx={{ position: "relative", flex: 1, minHeight: 0 }}>
                <Render3D uxFlavor={uxFlavor} />
                <MarkerRendererManager />
                <MarkerCreationMode />
                {sceneStatus.status === AsyncStatus.Success && view && scene && !disableHud ? <Hud /> : null}
                <Consent />
                <VersionAlert />
                <DockDragProvider>
                    <DockLayoutProvider>
                        <WindowManager />
                        <DockManager />
                    </DockLayoutProvider>
                </DockDragProvider>
            </Box>
        </Box>
    );
}

function ContextProviders({ children, uxFlavor }: { children: ReactNode; uxFlavor?: UxFlavor }) {
    const markerRendererRef = useRef<LibSvgRenderer | null>(null);
    const svgRendererRef = useRef<LibSvgRenderer | null>(null);
    const autoDockOnOpen = uxFlavor != null && !isMobileUxFlavor(uxFlavor);
    return (
        <ExplorerGlobalsProvider>
            <HighlightedProvider>
                <ObjectTreeProvider>
                    <HighlightCollectionsProvider>
                        <HiddenProvider>
                            <SelectionBasketProvider>
                                <FormsGlobalsProvider>
                                    <CameraStateProvider>
                                        <LastPickSampleProvider>
                                            <PickingSessionProvider>
                                                <ObjectGroupsProvider>
                                                    <ProcoreStampProvider>
                                                        <PropertiesTabsProvider>
                                                            <WindowsProvider autoDockOnOpen={autoDockOnOpen}>
                                                                <SidePanelsProvider>
                                                                    <DrawersProvider>
                                                                        <ToolbarProvider>
                                                                            <CoachmarksProvider>
                                                                                <SvgRendererContext.Provider
                                                                                    value={svgRendererRef}
                                                                                >
                                                                                    <MarkerRendererContext.Provider
                                                                                        value={markerRendererRef}
                                                                                    >
                                                                                        {children}
                                                                                    </MarkerRendererContext.Provider>
                                                                                </SvgRendererContext.Provider>
                                                                            </CoachmarksProvider>
                                                                        </ToolbarProvider>
                                                                    </DrawersProvider>
                                                                </SidePanelsProvider>
                                                            </WindowsProvider>
                                                        </PropertiesTabsProvider>
                                                    </ProcoreStampProvider>
                                                </ObjectGroupsProvider>
                                            </PickingSessionProvider>
                                        </LastPickSampleProvider>
                                    </CameraStateProvider>
                                </FormsGlobalsProvider>
                            </SelectionBasketProvider>
                        </HiddenProvider>
                    </HighlightCollectionsProvider>
                </ObjectTreeProvider>
            </HighlightedProvider>
        </ExplorerGlobalsProvider>
    );
}

function getUrlSearchQuery(): undefined | UrlSearchQuery {
    const params = new URLSearchParams(window.location.search);
    const searchQuery = params.get("search");
    const selectionOnly = params.get("selectionOnly") ?? "";
    const openWidgets = !(params.get("closeWidgets") ?? "");

    if (!searchQuery) {
        return;
    }

    try {
        const patterns = JSON.parse(searchQuery) as unknown;
        const patternArray = Array.isArray(patterns) ? patterns : [patterns];
        const validPatterns = patternArray.filter(
            (pattern): pattern is SearchPattern =>
                typeof pattern === "object" &&
                "property" in pattern &&
                Boolean(pattern.property) &&
                (("value" in pattern && Boolean(pattern.value)) || ("range" in pattern && Boolean(pattern.range))),
        );
        const includeDescendants = !["false", "0"].includes(params.get("includeChildObjects") ?? "");

        if (validPatterns.length) {
            return { pattern: validPatterns, options: { selectionOnly, openWidgets, includeDescendants } };
        }
    } catch {
        return { pattern: searchQuery, options: { selectionOnly, openWidgets, includeDescendants: true } };
    }
}
